import { ClientLoggingService } from '../../../shared/client-logging.service';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Params, Router, RouterModule, NavigationEnd } from '@angular/router';
import { Observable, Subject, from, of } from 'rxjs';
import { map, takeUntil, catchError, filter } from 'rxjs/operators';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { UiService } from '../../../shared/ui.service';
import { PlanReviewerService, GrpcServiceError } from '../../../shared/plan-reviewer.service';
import { ComplianceStateService } from '../../../shared/compliance-state.service';
import { DrawerComponent } from './drawer/drawer.component';
import { ProjectSettingsComponent } from '../settings/project-settings/project-settings.component';
import { FileViewComponent, FileViewTab } from '../file-view/file-view.component';
import { ArchitecturalPlanPage } from '../../../shared/models/architectural-plan-page.model';
import { Tab } from '../../../shared/models/tab.model';
import { TabService } from '../../../shared/tab.service';
import {
  HIGHLIGHT_ALSO_QUERY_PARAM,
  HIGHLIGHT_LABELS_QUERY_PARAM,
  HIGHLIGHT_QUERY_PARAM,
  HIGHLIGHT_ROTATION_QUERY_PARAM,
  HIGHLIGHT_UNITS_QUERY_PARAM,
  HighlightRegion,
  parseHighlightRegions,
  parseHighlightRotation,
  parseHighlightUnits,
} from '../../../shared/highlight-regions';
import { ErrorDialogComponent } from '../../shared/dialogs/error-dialog/error-dialog.component';
import { PermissionRefreshService } from '../../../shared/permission-refresh.service';
import { FirebaseAuthService } from '../../../shared/firebase-auth.service';
import { PlanRefreshService } from '../../../shared/plan-refresh.service';
import { PagePreloadService } from '../../../shared/page-preload.service';
import { ProjectCreationService } from '../../../shared/project-creation.service';
import { RegionHierarchyTreeNode } from '../../../../generated.commonjs/api_pb';
import { FilePageNavigationStatus } from '../../../../generated.commonjs/plan.reviewer_pb';
import { MatSnackBar } from '@angular/material/snack-bar';
import { lastValueFrom, take } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { HANDSET_DIALOG_QUERY } from '../../../shared/handset-dialog.service';
import { EnvironmentService } from '../../../shared/environment.service';

export type MainSelection =
  | { kind: 'project' }
  | { kind: 'file'; fileId: string; tab: FileViewTab }
  | { kind: 'page'; fileId: string; pageNumber: number; tab: string; regionPath?: string };

/** Primary project workspace layout containing the page drawer, PDF preview pane, and tabbed content area (compliance, tasks, etc.). */
@Component({
  selector: 'app-main-view',
  imports: [
    CommonModule,
    RouterModule,
    MatTabsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    DrawerComponent,
    ProjectSettingsComponent,
    FileViewComponent,
  ],
  templateUrl: './main-view.component.html',
  styleUrls: ['./main-view.component.css'],
  animations: [
    trigger('pageSlide', [
      transition(
        ':enter',
        [
          style({ transform: '{{ enterFrom }}', opacity: 0 }),
          animate('250ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateY(0)', opacity: 1 })),
        ],
        { params: { enterFrom: 'translateY(30%)' } }
      ),
      transition(':leave', [animate('200ms ease-in', style({ transform: '{{ leaveTo }}', opacity: 0 }))], {
        params: { leaveTo: 'translateY(-20%)' },
      }),
    ]),
  ],
})
export class MainViewComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private planReviewerService = inject(PlanReviewerService);
  private tabService = inject(TabService);
  private uiService = inject(UiService);
  private complianceStateService = inject(ComplianceStateService);
  private dialog = inject(MatDialog);
  private permissionRefreshService = inject(PermissionRefreshService);
  private firebaseAuthService = inject(FirebaseAuthService);
  private planRefreshService = inject(PlanRefreshService);
  private pagePreloadService = inject(PagePreloadService);
  private snackBar = inject(MatSnackBar);
  private projectCreationService = inject(ProjectCreationService);
  private breakpointObserver = inject(BreakpointObserver);

  drawerOpened = false; // Start closed by default
  private environmentService = inject(EnvironmentService);
  isHandset = false;
  plan$!: Observable<any>;
  pages$: Observable<ArchitecturalPlanPage[]> = new Observable<ArchitecturalPlanPage[]>();
  activePlanPage: ArchitecturalPlanPage | null = null;
  activeFileId: string | null = null; // NEW: Track active file ID
  pages: ArchitecturalPlanPage[] = [];
  projectName = '';
  planId$!: Observable<string>;
  planLoading$!: Observable<boolean>;
  hasError = false;
  planResolved = false;
  showingProjectView = false;
  powerUserMode = false;
  activeRegionPath: string | null = null;
  showingFileView = false;
  fileViewTab: FileViewTab = 'overview';
  mainSelection: MainSelection = { kind: 'project' };
  fileNodeSelectionEnabled = false;
  filePageStatuses = new Map<number, FilePageNavigationStatus.AsObject>();

  tabs$: Observable<Tab[]>;
  activeTabIndex$: Observable<number>;
  tableOfContentsWarnings$: Observable<boolean>;

  // Track checked state separately from protobuf page objects.
  // Issue #1652: keyed by fileId so a page number stays bound to its own file
  // in multi-file projects.
  checkedPagesByFile = new Map<string, Set<number>>();

  // Two-element page transition using Angular animations (Issue #1172).
  // renderKey is tracked by @for — incrementing it destroys the old element
  // (:leave) and creates a new one (:enter), both co-existing during animation.
  // Wrapped in a stable array to avoid NG0956 (re-creation on every CD cycle).
  renderKeys = [0];
  get renderKey(): number {
    return this.renderKeys[0];
  }
  slideParams = { enterFrom: 'translateY(30%)', leaveTo: 'translateY(-20%)' };
  private previousPageNumber: number | null = null;
  private readonly PAGE_UPDATE_DEBOUNCE_MS = 50;
  private pageUpdateTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private pendingPageUpdate: { page: ArchitecturalPlanPage; fileId: string } | null = null;

  private destroy$ = new Subject<void>();
  private currentPlanId: string | null = null;
  private lastFileTabs = new Map<string, FileViewTab>();

  constructor() {
    this.tabs$ = this.tabService.tabs$;
    this.activeTabIndex$ = this.tabService.activeTabIndex$;
    this.tableOfContentsWarnings$ = this.tabService.tableOfContentsWarnings$;
    this.fileNodeSelectionEnabled = this.environmentService.isFileNodeSelectionEnabled?.() ?? false;
    this.planId$ = this.route.params.pipe(map(params => params['planId'] || ''));
    this.planLoading$ = this.uiService.planLoading$;

    this.breakpointObserver
      .observe(HANDSET_DIALOG_QUERY)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ matches }) => {
        this.isHandset = matches;
        if (matches) {
          this.drawerOpened = false;
        }
      });

    this.uiService.toggleDrawer$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.toggleDrawer();
    });
  }

  ngOnInit(): void {
    // Listen for permission changes and handle cache clearing
    this.permissionRefreshService.permissionChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async ({ type, projectId, userEmail }) => {
        ClientLoggingService.log(
          'INFO',
          `[MainView] Permission change detected: ${type} ${userEmail} for project ${projectId}`
        );

        // If current project is affected, clear caches and check access
        if (projectId === this.currentPlanId) {
          ClientLoggingService.log('INFO', `[MainView] Current project affected by permission change, clearing caches`);
          this.planReviewerService.clearPlanDataCache();
          this.complianceStateService.clearPlanStates(projectId);

          // Only redirect if the *current user* was the one removed.
          const currentUser = await lastValueFrom(this.firebaseAuthService.user$.pipe(take(1)));
          if (type === 'remove' && userEmail === currentUser?.email) {
            ClientLoggingService.log(
              'INFO',
              `[MainView] Current user was removed from the project, redirecting to home`
            );
            this.router.navigate(['/']);
          }
        }
      });

    // Listen for plan refresh events (e.g., after page ingestion)
    this.planRefreshService.planRefresh$.pipe(takeUntil(this.destroy$)).subscribe(({ planId, reason }) => {
      ClientLoggingService.log('INFO', `[MainView] Plan refresh requested for ${planId}: ${reason}`);

      // Only refresh if it's the current plan
      if (planId === this.currentPlanId) {
        ClientLoggingService.log('INFO', `[MainView] Refreshing current plan data and clearing caches`);
        this.planReviewerService.clearPlanDataCache();

        // Reload the plan data by re-triggering the observable
        this.loadPlanData(planId, true);
      }
    });

    // Listen to router events to handle both parent and child route params
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Get parent params (planId)
        const planId = this.route.snapshot.params['planId'];

        // Get child params (fileId, pageNumber, tab, regionPath) from first child
        const firstChild = this.route.snapshot.firstChild;
        const fileId = firstChild ? firstChild.params['fileId'] : undefined;
        const pageNumber = firstChild ? +firstChild.params['pageNumber'] : NaN;
        const tab = firstChild ? firstChild.params['tab'] : undefined;
        const regionPath = firstChild ? firstChild.params['regionPath'] : undefined;
        const childQueryParams = firstChild ? firstChild.queryParams : {};
        const queryParams = {
          ...(this.route.snapshot.queryParams || {}),
          ...childQueryParams,
        };

        ClientLoggingService.log(
          'INFO',
          `MainView: Route params - planId: ${planId}, fileId: ${fileId}, pageNumber: ${pageNumber}, tab: ${tab}, regionPath: ${regionPath}, URL: ${this.router.url}`
        );

        // Handle highlight query parameter (Issue #292)
        if (queryParams['highlight']) {
          const regions = this.parseHighlightRegions(queryParams);
          this.tabService.setHighlightRegions(
            regions,
            parseHighlightRotation(queryParams[HIGHLIGHT_ROTATION_QUERY_PARAM]),
            parseHighlightUnits(queryParams[HIGHLIGHT_UNITS_QUERY_PARAM])
          );
          ClientLoggingService.log('INFO', '[MainView] Setting highlight regions from URL:', regions);
        } else if (!regionPath) {
          this.tabService.setHighlightBox(null);
        }

        // Handle legacy URL redirects (details → overview#explanation, raw-markdown → overview#transcript)
        if (this.handleLegacyUrls(planId, fileId, pageNumber, tab)) {
          return; // Redirect triggered, skip rest of processing
        }

        if (planId !== this.currentPlanId) {
          this.switchProject(planId);
        }

        const isSettingsRoute = this.router.url.includes('/settings');
        const isTasksRoute = this.router.url.includes('/tasks') || this.router.url.includes('/cat-graphs');
        const childData = (firstChild?.data ?? {}) as Record<string, unknown>;
        if (
          !this.updateSelectionFromRoute(
            planId,
            fileId,
            pageNumber,
            tab,
            regionPath,
            childData,
            isSettingsRoute || isTasksRoute
          )
        ) {
          return;
        }

        // Load plan data to get the pages for the drawer
        this.loadPlanData(planId);

        // Update active page if we're on a page route
        if (!this.showingProjectView && !this.showingFileView && !isNaN(pageNumber)) {
          // Will be set properly after plan data loads in loadPlanData
          ClientLoggingService.log('INFO', `MainView: Navigating to page ${pageNumber}, tab: ${tab}`);
        }
      });

    // Trigger initial load
    const initialPlanId = this.route.snapshot.params['planId'];
    if (initialPlanId) {
      const firstChild = this.route.snapshot.firstChild;
      const initialFileId = firstChild ? firstChild.params['fileId'] : undefined;
      const initialPageNumber = firstChild ? +firstChild.params['pageNumber'] : NaN;
      const initialTab = firstChild ? firstChild.params['tab'] : undefined;
      const childQueryParams = firstChild ? firstChild.queryParams : {};
      const initialQueryParams = {
        ...(this.route.snapshot.queryParams || {}),
        ...childQueryParams,
      };

      ClientLoggingService.log(
        'INFO',
        `MainView: Initial load - planId: ${initialPlanId}, fileId: ${initialFileId}, pageNumber: ${initialPageNumber}, tab: ${initialTab}, queryParams:`,
        initialQueryParams
      );

      // Handle highlight query parameter on initial load (Issue #292)
      if (initialQueryParams['highlight']) {
        const regions = this.parseHighlightRegions(initialQueryParams);
        this.tabService.setHighlightRegions(
          regions,
          parseHighlightRotation(initialQueryParams[HIGHLIGHT_ROTATION_QUERY_PARAM]),
          parseHighlightUnits(initialQueryParams[HIGHLIGHT_UNITS_QUERY_PARAM])
        );
        ClientLoggingService.log('INFO', '[MainView] Setting highlight regions from initial URL:', regions);
      } else {
        this.tabService.setHighlightBox(null);
      }

      this.currentPlanId = initialPlanId;
      const isSettingsRoute = this.router.url.includes('/settings');
      const isTasksRoute = this.router.url.includes('/tasks') || this.router.url.includes('/cat-graphs');
      const childData = (firstChild?.data ?? {}) as Record<string, unknown>;
      if (
        !this.updateSelectionFromRoute(
          initialPlanId,
          initialFileId,
          initialPageNumber,
          initialTab,
          firstChild?.params['regionPath'],
          childData,
          isSettingsRoute || isTasksRoute
        )
      ) {
        return;
      }
      this.loadPlanData(initialPlanId);
    }
  }

  ngOnDestroy(): void {
    if (this.pageUpdateTimeoutId) {
      clearTimeout(this.pageUpdateTimeoutId);
      this.pageUpdateTimeoutId = null;
    }
    this.pendingPageUpdate = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private openErrorDialog(error: any): void {
    let title = 'Error Loading Plan';
    let message = 'An unexpected error occurred. Please try again later or contact support.';
    let icon = 'error';
    let showRequestAccess = false;

    if (error instanceof GrpcServiceError) {
      message = error.message;
      if (error.code === 7) {
        title = 'Access Denied';
        // Permission failures may contain resource details belonging to another account.
        message = "You don't have permission to view this architectural project.";
        icon = 'lock';
        showRequestAccess = true;
      } else if (error.code === 5) {
        title = 'Project Not Found';
        message =
          'The requested project could not be found. ' +
          'It may have been deleted or the project ID may be invalid. ' +
          'Please verify the URL or check with the project owner.';
        icon = 'search_off';
      }
    } else if (error.message) {
      message = error.message;
    }

    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      data: { title, message, icon, showRequestAccess },
      width: '600px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'retry') {
        this.retryLoadPlan();
      } else {
        this.router.navigate(['/']);
      }
    });
  }

  retryLoadPlan(): void {
    const currentPlanId = this.currentPlanId;
    if (currentPlanId) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/projects', currentPlanId]);
      });
    } else {
      this.router.navigate(['/']);
    }
  }

  toggleDrawer(): void {
    this.drawerOpened = !this.drawerOpened;
  }

  onFileActivated(event: { fileId: string }): void {
    if (!this.fileNodeSelectionEnabled) {
      return;
    }
    const planId = this.route.snapshot.params['planId'];
    const tab = this.lastFileTabs.get(event.fileId) ?? 'overview';
    if (this.activeFileId !== event.fileId) {
      this.filePageStatuses = new Map();
    }
    if (this.isHandset) {
      this.drawerOpened = false;
    }
    void this.router.navigate(['/projects', planId, 'files', event.fileId, tab]);
  }

  onFilePageStatusesChange(statuses: FilePageNavigationStatus.AsObject[]): void {
    this.filePageStatuses = new Map(statuses.map(status => [status.pageNumber, status]));
  }

  /**
   * Derives the only selection state from the active route. Convenience fields remain synchronized
   * for existing page components while migration to MainSelection is in progress.
   */
  private updateSelectionFromRoute(
    planId: string,
    fileId: string | undefined,
    pageNumber: number,
    tab: string | undefined,
    regionPath: string | undefined,
    childData: Record<string, unknown>,
    isProjectRoute: boolean
  ): boolean {
    const configuredFileTab = childData['fileTab'];
    const fileTab =
      configuredFileTab === 'overview' || configuredFileTab === 'compliance' ? configuredFileTab : undefined;

    if (fileTab && !this.fileNodeSelectionEnabled) {
      this.showingProjectView = true;
      this.showingFileView = false;
      this.mainSelection = { kind: 'project' };
      this.activeFileId = null;
      this.activePlanPage = null;
      void this.router.navigate(['/projects', planId, 'settings'], { replaceUrl: true });
      return false;
    }

    if (isProjectRoute) {
      this.showingProjectView = true;
      this.showingFileView = false;
      this.mainSelection = { kind: 'project' };
      this.activeFileId = null;
      this.activePlanPage = null;
      this.activeRegionPath = null;
      this.filePageStatuses = new Map();
    } else if (fileTab && fileId) {
      if (this.activeFileId !== fileId) {
        this.filePageStatuses = new Map();
      }
      this.showingProjectView = false;
      this.showingFileView = true;
      this.fileViewTab = fileTab;
      this.lastFileTabs.set(fileId, fileTab);
      this.mainSelection = { kind: 'file', fileId, tab: fileTab };
      this.activeFileId = fileId;
      this.activePlanPage = null;
      this.activeRegionPath = null;
      this.tabService.removeDynamicTabs();
    } else if (fileId && Number.isFinite(pageNumber)) {
      if (this.activeFileId !== fileId) {
        this.filePageStatuses = new Map();
      }
      this.showingProjectView = false;
      this.showingFileView = false;
      this.mainSelection = {
        kind: 'page',
        fileId,
        pageNumber,
        tab: tab ?? 'overview',
        ...(regionPath ? { regionPath } : {}),
      };
      this.activeFileId = fileId;
    }

    if (this.isHandset && (this.showingProjectView || this.showingFileView)) {
      this.drawerOpened = false;
    }
    return true;
  }

  onPageActivated(event: { page: ArchitecturalPlanPage; fileId: string }): void {
    const newPageNumber = event.page.getPageNumber();

    this.pagePreloadService.preloadPageData(event.page);
    this.pagePreloadService.preloadNeighborPages(event.page, this.pages);

    this.triggerPageTransition(newPageNumber);

    this.activePlanPage = event.page;
    this.activeFileId = event.fileId;
    this.showingProjectView = false;
    this.showingFileView = false;
    this.mainSelection = {
      kind: 'page',
      fileId: event.fileId,
      pageNumber: newPageNumber,
      tab: this.tabService.getTabLabel(this.tabService.getActiveTabIndex()),
    };
    if (this.activeRegionPath) {
      this.activeRegionPath = null;
      this.tabService.setActiveRegion(null);
    }
    this.tabService.setActivePlanPage(event.page);
    this.planReviewerService.setSelectedPage(event.page);

    // Debounce route navigation and tab management
    if (this.pageUpdateTimeoutId) {
      clearTimeout(this.pageUpdateTimeoutId);
      this.pageUpdateTimeoutId = null;
    }
    this.pendingPageUpdate = event;
    this.pageUpdateTimeoutId = setTimeout(() => {
      if (this.pendingPageUpdate) {
        this.applyPageUpdate(this.pendingPageUpdate);
        this.pendingPageUpdate = null;
      }
      this.pageUpdateTimeoutId = null;
    }, this.PAGE_UPDATE_DEBOUNCE_MS);
  }

  /**
   * Makes the region named by the address bar the active region (Issue #2445).
   *
   * After a reload, or when a region link is opened, the route names a region but nothing has
   * selected it yet, so the Preview has no box to highlight. The drawer reports the region once it
   * has loaded the page's hierarchy. The address bar already names it, so there is no navigation.
   */
  onRegionRestored(region: RegionHierarchyTreeNode.AsObject): void {
    if (this.activeRegionPath !== region.path) return;
    if (this.tabService.getActiveRegion()?.path === region.path) return;
    this.tabService.setActiveRegion(region);
  }

  /** Handles region selection from the drawer (Issue #1228). */
  onRegionActivated(event: {
    page: ArchitecturalPlanPage;
    fileId: string;
    region: RegionHierarchyTreeNode.AsObject;
  }): void {
    ClientLoggingService.log(
      'INFO',
      `[MainViewComponent] Region activated: ${event.region.slug} on page ${event.page.getPageNumber()}`
    );
    this.activeRegionPath = event.region.path;
    this.tabService.setActiveRegion(event.region);

    const planId = this.route.snapshot.params['planId'];
    const currentTab = this.tabService.getTabLabel(this.tabService.getActiveTabIndex());
    const regionSegments = event.region.path.split('/');
    this.router.navigate(
      ['/projects', planId, 'files', event.fileId, 'pages', event.page.getPageNumber(), ...regionSegments, currentTab],
      { queryParamsHandling: 'preserve' }
    );
  }

  onRegionHovered(region: RegionHierarchyTreeNode.AsObject | null): void {
    this.tabService.setHoveredRegion(region);
  }

  /**
   * Apply the debounced portion of the page update (Issue #565): route
   * navigation and dynamic tab management. Content rendering is triggered
   * immediately in onPageActivated so it starts during the slide-out phase.
   */
  private applyPageUpdate(event: { page: ArchitecturalPlanPage; fileId: string }): void {
    // Issue #660: Dynamic tabs are per-page compliance report sections (e.g.
    // "IBC2021 Ch10 Sec1014.6"). They must be removed on page change because
    // their data is specific to the previous page. When one was active, fall
    // back to the parent Compliance tab for the new page.
    let currentTab = this.tabService.getTabLabel(this.tabService.getActiveTabIndex());
    const wasComplianceReportTabActive = this.tabService.isActiveDynamicTab();
    this.tabService.removeDynamicTabs();

    if (wasComplianceReportTabActive) {
      currentTab = 'compliance';
    }

    const currentParams = this.route.snapshot.params;
    this.router.navigate(
      ['/projects', currentParams['planId'], 'files', event.fileId, 'pages', event.page.getPageNumber(), currentTab],
      { queryParamsHandling: 'preserve' }
    );
  }

  onCheckedPagesChanged(checkedPagesByFile: Map<string, Set<number>>): void {
    this.checkedPagesByFile = checkedPagesByFile;
  }

  onTabChange(event: any): void {
    const tabLabel = this.tabService.getTabLabel(event.index);
    this.tabService.setActiveTab(event.index);

    // Get parent and child params
    const planId = this.route.snapshot.params['planId'];
    const firstChild = this.route.snapshot.firstChild;
    // Adding/resolving a dynamic compliance tab updates the selected index programmatically. The
    // Material tab group emits selectedTabChange for that update even though the router already
    // points at the same tab. Starting another navigation here races the navigation that supplied
    // file-summary query/state context and can replace it with a context-free URL.
    if (firstChild?.params['tab'] === tabLabel) {
      return;
    }
    const pageNumber = firstChild ? firstChild.params['pageNumber'] : this.activePlanPage?.getPageNumber() || 1;
    const fileId = firstChild ? firstChild.params['fileId'] : this.activeFileId || '1';
    const regionPath: string | undefined = firstChild ? firstChild.params['regionPath'] : undefined;

    const regionSegments = regionPath ? regionPath.split('/') : [];
    const basePath = ['/projects', planId, 'files', fileId, 'pages', pageNumber, ...regionSegments];

    if (tabLabel === 'overview') {
      this.router.navigate([...basePath, 'overview'], {
        fragment: 'explanation',
        queryParamsHandling: 'preserve',
      });
    } else {
      this.router.navigate([...basePath, tabLabel], { queryParamsHandling: 'preserve' });
    }
  }

  /**
   * Redirects legacy tab URLs to new fragment-based structure
   * Returns true if a redirect was triggered (to skip further processing)
   */
  private handleLegacyUrls(
    planId: string,
    fileId: string | undefined,
    pageNumber: number,
    tab: string | undefined
  ): boolean {
    if (!planId || !pageNumber || isNaN(pageNumber)) return false;

    const effectiveFileId = fileId || '1';

    // Redirect legacy tabs to overview with fragments
    if (tab === 'details') {
      ClientLoggingService.log('INFO', '[MainView] Redirecting legacy /details URL to /overview#explanation');
      this.router.navigate(['/projects', planId, 'files', effectiveFileId, 'pages', pageNumber, 'overview'], {
        fragment: 'explanation',
        replaceUrl: true,
        queryParamsHandling: 'preserve', // Preserve query params like highlight
      });
      return true;
    } else if (tab === 'raw-markdown') {
      ClientLoggingService.log('INFO', '[MainView] Redirecting legacy /raw-markdown URL to /overview#transcript');
      this.router.navigate(['/projects', planId, 'files', effectiveFileId, 'pages', pageNumber, 'overview'], {
        fragment: 'transcript',
        replaceUrl: true,
        queryParamsHandling: 'preserve', // Preserve query params like highlight
      });
      return true;
    }

    return false;
  }

  /** Issue #1878: only report tabs get a close button; the fixed application tabs stay put. */
  isClosableTab(tab: Tab): boolean {
    return TabService.isDynamicTab(tab);
  }

  /**
   * Closes the report tab at the given position. The button lives inside the tab header, so the
   * click is stopped here; otherwise it would also select the tab that is being removed.
   */
  closeTab(index: number, event?: Event): void {
    event?.stopPropagation();
    this.tabService.removeTab(index);
  }

  /**
   * Two-element page transition (Issue #1172).
   * Incrementing renderKey causes Angular's @for to destroy the old element
   * (triggering :leave animation) and create a new one (triggering :enter).
   * Both coexist during the animation — old content slides out with its own
   * DOM while new content slides in fresh. The parent .main-viewport clips
   * via overflow:hidden, keeping app bar and drawers static.
   */
  private triggerPageTransition(newPageNumber: number): void {
    if (this.previousPageNumber === null || this.previousPageNumber === newPageNumber) {
      this.previousPageNumber = newPageNumber;
      return;
    }

    const goingForward = newPageNumber > this.previousPageNumber;
    this.previousPageNumber = newPageNumber;

    this.slideParams = goingForward
      ? { enterFrom: 'translateY(30%)', leaveTo: 'translateY(-20%)' }
      : { enterFrom: 'translateY(-30%)', leaveTo: 'translateY(20%)' };

    this.renderKeys = [this.renderKey + 1];
  }

  /**
   * Read the highlighted regions from the URL (Issue #292, several regions since Issue #2254).
   *
   * `highlight` carries the first box (`x,y,width,height`), `highlightAlso` the others separated
   * by `;`, and `highlightLabels` optionally names them all in order. A value that cannot be read
   * yields no regions, which clears the highlight, as an unreadable single box always did.
   */
  private parseHighlightRegions(queryParams: Params): HighlightRegion[] {
    const regions = parseHighlightRegions(
      queryParams[HIGHLIGHT_QUERY_PARAM],
      queryParams[HIGHLIGHT_ALSO_QUERY_PARAM],
      queryParams[HIGHLIGHT_LABELS_QUERY_PARAM]
    );
    if (regions.length === 0) {
      ClientLoggingService.log('WARN', '[MainView] Invalid highlight parameter:', queryParams[HIGHLIGHT_QUERY_PARAM]);
    }
    return regions;
  }

  /**
   * No longer needed - checked state is now tracked in checkedPagesByFile
   * which persists independently of page objects.
   * Keeping this as a pass-through for now to avoid breaking the call site.
   */
  private preserveCheckedState(
    newPages: ArchitecturalPlanPage[],
    oldPages: ArchitecturalPlanPage[]
  ): ArchitecturalPlanPage[] {
    // Checked state is now maintained in this.checkedPagesByFile
    // which persists across page reloads
    ClientLoggingService.log(
      'DEBUG',
      `[MainView] preserveCheckedState: ${oldPages.length} old pages -> ${newPages.length} new pages (checked state preserved via Set)`
    );
    return newPages;
  }

  /**
   * Resets all project-scoped state when navigating to a different project.
   * Clears caches, checked pages, and stale page data so loadPlanData()
   * fetches fresh data instead of short-circuiting on the old pages array.
   */
  private switchProject(planId: string): void {
    ClientLoggingService.log(
      'DEBUG',
      `MainView: Plan ID changed from ${this.currentPlanId} to ${planId}. Clearing caches.`
    );
    this.planReviewerService.clearPlanDataCache();
    if (this.currentPlanId) {
      this.complianceStateService.clearPlanStates(this.currentPlanId);
    }
    this.checkedPagesByFile = new Map();
    this.pages = [];
    this.projectName = '';
    this.activeFileId = null;
    this.activePlanPage = null;
    this.filePageStatuses = new Map();

    this.previousPageNumber = null;
    if (this.pageUpdateTimeoutId) {
      clearTimeout(this.pageUpdateTimeoutId);
      this.pageUpdateTimeoutId = null;
    }
    this.pendingPageUpdate = null;

    // Remove dynamic compliance-report tabs from the previous project (Issue #660)
    this.tabService.removeDynamicTabs();

    this.currentPlanId = planId;
  }

  /**
   * Loads plan data for the specified plan ID.
   * Extracted from ngOnInit to allow reuse for refresh operations.
   */
  private loadPlanData(planId: string, noCache = false): void {
    if (this.currentPlanId === planId && this.pages.length > 0 && !noCache) {
      ClientLoggingService.log(
        'INFO',
        `[MainView] Plan data for ${planId} already loaded and no cache refresh requested. Skipping fetch.`
      );

      // Still need to handle active page and tabs based on route
      this.handleActivePageAndTabs();
      return;
    }

    this.hasError = false;
    this.planResolved = false;
    this.uiService.setPlanLoading(true);

    this.plan$ = from(this.planReviewerService.getArchitecturalPlan(planId, noCache)).pipe(
      map((response: any) => {
        // NOTE(Issue #738 follow-up): these used to go through ClientLoggingService.log(),
        // which recursively sanitizes and JSON.stringify()s its arguments synchronously on the
        // main thread before handing off an async HttpClient POST. Logging the *entire* raw
        // gRPC response (every page's full transcript, potentially tens of MB for large
        // projects) through that synchronous sanitize/stringify step could block the UI thread
        // long enough to make the whole tab unresponsive. Keep this diagnostic detail local via
        // console.debug(), which the browser only serializes lazily if the log line is actually
        // expanded in DevTools.
        console.debug('[MainView] Raw gRPC response:', response);
        console.debug('[MainView] Response type:', response?.constructor?.name);
        const pages = response?.getPagesList();
        ClientLoggingService.log('INFO', '[MainView] Pages count:', pages?.length);
        if (pages && pages.length > 0) {
          console.debug('[MainView] First page:', pages[0]);
          console.debug(
            '[MainView] First page toObject():',
            (pages[0] as any).toObject ? (pages[0] as any).toObject() : 'NO toObject'
          );
          console.debug('[MainView] First page getExplanationMarkdown():', pages[0].getExplanationMarkdown());
          console.debug('[MainView] First page getPageMarkdown():', pages[0].getPageMarkdown());
        }
        return response;
      }),
      catchError(error => {
        this.uiService.setPlanLoading(false);
        this.hasError = true;
        this.planResolved = true;
        this.drawerOpened = false;
        this.openErrorDialog(error);
        return of(null);
      }),
      takeUntil(this.destroy$)
    );

    this.pages$ = this.plan$.pipe(map(plan => plan?.getPagesList() || []));

    this.pages$.subscribe(pages => {
      if (this.hasError) return;

      this.uiService.setPlanLoading(false);
      this.planResolved = true;

      this.pages = this.preserveCheckedState(pages, this.pages);
      // A side-by-side table of contents is useful on desktop, but on handsets it must remain
      // closed after asynchronous plan loading so it cannot cover Settings or page actions.
      this.drawerOpened = !this.isHandset;

      // Handle active page and tabs in a separate method for reuse
      this.handleActivePageAndTabs();
    });

    // Issue #1228: Load power_user_mode to gate the References tab
    this.loadPowerUserMode(planId);
  }

  private async loadPowerUserMode(planId: string): Promise<void> {
    this.tabService.loadTableOfContentsWarnings(planId);
    this.projectName = planId;
    try {
      const result = await this.projectCreationService.getProjectMetadata(planId);
      this.projectName = result?.metadata?.project_name?.trim() || planId;
      const enabled = result?.metadata?.power_user_mode ?? false;
      this.powerUserMode = enabled;
      this.tabService.setPowerUserMode(enabled);
      this.tabService.loadHighlightFeatherPct(planId);
    } catch {
      this.powerUserMode = false;
      this.tabService.setPowerUserMode(false);
    }
  }

  /**
   * Sets the active page and tabs based on the current route.
   * Extracted from loadPlanData for reuse when skipping data fetch.
   */
  private handleActivePageAndTabs(): void {
    if (!this.showingProjectView && !this.showingFileView) {
      // Read child route params for file ID, page number, tab, and region
      const firstChild = this.route.snapshot.firstChild;
      const fileId = firstChild ? firstChild.params['fileId'] : undefined;
      const pageNumber = firstChild ? +firstChild.params['pageNumber'] : NaN;
      const tab = firstChild ? firstChild.params['tab'] : undefined;
      const regionPath: string | undefined = firstChild ? firstChild.params['regionPath'] : undefined;
      // Preserve the canonical cumulative path from a direct deep link so the drawer can reveal
      // and select the exact region branch before the user interacts with the tree.
      this.activeRegionPath = regionPath || null;

      ClientLoggingService.log(
        'INFO',
        `[MainView] Setting active page from params - fileId: ${fileId}, pageNumber: ${pageNumber}, tab: ${tab}, regionPath: ${regionPath}`
      );

      // Find the page that matches both the file ID and page number
      const newPage = this.findPageByFileAndNumber(fileId, pageNumber) || this.pages[0] || null;

      // Issue #660: When the page changes via route navigation (e.g. browser
      // back/forward), clean up dynamic tabs that belong to the old page.
      const pageChanged =
        newPage && this.activePlanPage && newPage.getPageNumber() !== this.activePlanPage.getPageNumber();
      if (pageChanged) {
        this.tabService.removeDynamicTabs();
      }

      this.activePlanPage = newPage;

      // Update active file ID
      if (fileId) {
        this.activeFileId = fileId;
      }

      if (this.activePlanPage) {
        // Initialize previous page number for animation tracking (Issue #565)
        // Don't animate on initial load
        this.previousPageNumber = this.activePlanPage.getPageNumber();

        // Only call setActivePlanPage (which clears region) when no region
        // is in the URL. Otherwise the region state would be cleared.
        if (!regionPath) {
          this.tabService.setActivePlanPage(this.activePlanPage);
        } else {
          // Set page without clearing region
          this.tabService.setActivePlanPageKeepRegion(this.activePlanPage);
        }
        this.planReviewerService.setSelectedPage(this.activePlanPage);
        ClientLoggingService.log(
          'INFO',
          `[MainView] Set active page to: ${this.activePlanPage.getPageNumber()} from file ${fileId}`
        );
        ClientLoggingService.log(
          'INFO',
          `[MainView] Active page pageFolderPath:`,
          this.activePlanPage.getPageFolderPath()
        );
      }

      const tabIndex = this.tabService.getTabIndexByTabParam(tab);

      // Issue #660: If the URL contains a section-level deep link (e.g.
      // .../pages/6/IBC2018P6_Ch11_Sec1105), show a loading placeholder tab
      // and fetch compliance data directly to resolve it. We can't rely on
      // ComplianceComponent because Angular's lazy tab rendering may not
      // instantiate it while the loading tab is active.
      if (tabIndex === -1 && TabService.isDynamicTabParam(tab)) {
        this.tabService.addLoadingTab(tab!);
        this.resolveComplianceSectionDeepLink(tab!);
      } else {
        this.tabService.setActiveTab(tabIndex !== -1 ? tabIndex : 0);
      }
    } else {
      // Project and file routes have no active page.
      this.activePlanPage = null;
      // Reset animation state so next page navigation starts fresh (Issue #565)
      this.previousPageNumber = null;
      ClientLoggingService.log('INFO', `[MainView] Cleared active page (showing project or file view)`);
    }
  }

  /**
   * Issue #660: Fetch compliance data directly and resolve a section deep link.
   * Populates the loading placeholder tab on success or removes it on failure.
   */
  private async resolveComplianceSectionDeepLink(sectionId: string): Promise<void> {
    if (!this.activePlanPage) return;

    try {
      const data = await this.planReviewerService.getPageComplianceReport(this.activePlanPage);
      const tabIndex = this.tabService.getTabIndexByTabParam(sectionId);
      if (tabIndex === -1) return;

      const section = data?.sections?.find((s: { sectionId: string }) => s.sectionId === sectionId);
      if (section) {
        this.tabService.updateTabWithContent(tabIndex, section);
      } else {
        this.tabService.removeTab(tabIndex);
        this.snackBar.open(`Compliance report for "${sectionId}" is not available for this page.`, 'Dismiss', {
          duration: 5000,
        });
      }
    } catch (err) {
      ClientLoggingService.log('ERROR', '[MainView] Error resolving compliance deep link:', err);
      const tabIndex = this.tabService.getTabIndexByTabParam(sectionId);
      if (tabIndex !== -1) {
        this.tabService.removeTab(tabIndex);
      }
      this.snackBar.open('Failed to load compliance report.', 'Dismiss', { duration: 5000 });
    }
  }

  /**
   * Finds a page by file ID and page number from the hierarchical structure.
   */
  private findPageByFileAndNumber(fileId: string | undefined, pageNumber: number): ArchitecturalPlanPage | null {
    if (!fileId || isNaN(pageNumber)) {
      return null;
    }

    // One lookup runs on every navigation, over every page of the project (hundreds on a large
    // set). It used to dump every page's details and log each candidate it checked, which cost
    // about 0.2 s of main-thread time per region click even with DevTools closed (Issue #3300;
    // the logging had already moved off ClientLoggingService for #738). It now logs once.
    const foundPage =
      this.pages.find(page => {
        const pageFolderPath = page.getPageFolderPath();
        const modernPathMatch = pageFolderPath.match(/\/files\/(\d+)\/pages\/\d+\/?$/);
        if (modernPathMatch) {
          return modernPathMatch[1] === fileId && page.getPageNumber() === pageNumber;
        }
        // A legacy page folder has no file segment and belongs to file "1".
        const isLegacyPath = !pageFolderPath.includes('/files/') && /\/pages\/\d+\/?$/.test(pageFolderPath);
        return isLegacyPath && fileId === '1' && page.getPageNumber() === pageNumber;
      }) || null;

    if (foundPage) {
      ClientLoggingService.log('INFO', `[MainView] Found page ${pageNumber} in file ${fileId}:`, {
        title: foundPage.getTitle(),
        pageId: foundPage.getPageId(),
        pageFolderPath: foundPage.getPageFolderPath(),
      });
    } else {
      ClientLoggingService.log(
        'INFO',
        `[MainView] No page ${pageNumber} in file ${fileId} among ${this.pages.length} pages`
      );
    }

    return foundPage;
  }
}
